import { type AdfSmartLink } from '@confluence/connectors/entry-points/extractSmartLinksFromAdf.ts';

import { matchProviderKey } from '../providerPatterns.ts';
import { type QualifyingProduct } from '../thirdPartyPromptTypes.ts';

import { resolveProviderKeyToAppId } from './resolveProviderKeyToAppId.ts';

type ProductReferences = {
	count: number;
	firstOccurrenceIndex: number;
};

/** How often and how early the page's Atlassian Document Format references each product. */
const tallyByAppId = (links: readonly AdfSmartLink[]): Map<string, ProductReferences> => {
	const referencesByAppId = new Map<string, ProductReferences>();

	for (const { url, count, occurrenceIndex } of links) {
		const providerKey = matchProviderKey(url);
		const appId = providerKey ? resolveProviderKeyToAppId(providerKey) : undefined;

		if (appId) {
			const references = referencesByAppId.get(appId);

			referencesByAppId.set(appId, {
				count: (references?.count ?? 0) + count,
				firstOccurrenceIndex: references
					? Math.min(references.firstOccurrenceIndex, occurrenceIndex)
					: occurrenceIndex,
			});
		}
	}

	return referencesByAppId;
};

/**
 * The candidate the page references most. `undefined` when the walk matches none of them, which the
 * caller reads as "restore the first-party card".
 */
export const chooseMostReferencedProduct = (
	candidates: readonly QualifyingProduct[],
	links: readonly AdfSmartLink[],
): QualifyingProduct | undefined => {
	const referencesByAppId = tallyByAppId(links);

	let winner: QualifyingProduct | undefined;
	let bestCount = 0;
	let bestOccurrenceIndex = Number.POSITIVE_INFINITY;

	candidates.forEach((candidate) => {
		const references = referencesByAppId.get(candidate.appId);

		if (!references) {
			return;
		}

		const winsTieByPageOrder =
			references.count === bestCount && references.firstOccurrenceIndex < bestOccurrenceIndex;

		if (references.count > bestCount || winsTieByPageOrder) {
			winner = candidate;
			bestCount = references.count;
			bestOccurrenceIndex = references.firstOccurrenceIndex;
		}
	});

	return winner;
};
